import * as React from "react";

import {
  PlpSelectedFacetOption,
  PresentationFacet,
  ResponseFacetValue,
} from "../../model";

import { useProductListPresentationModelFacets } from "./product-list-presentation-model-provider";
import {
  useProductListState,
  useProductListState_asController,
} from "./product-list-state-provider";

interface ProductListController_ForFiltering_ConsumerInterface {
  handleActionFilterOption: (
    facet: PresentationFacet,
    option: ResponseFacetValue
  ) => void;
  handleRemoveSelectedFilterOption: (
    selectedOption: PlpSelectedFacetOption
  ) => void;
  handleRemoveAllSelectedOptions: () => void;
  handleRemoveSelectedOptionsInBulk: (
    selectedOptionsToRemove: PlpSelectedFacetOption[]
  ) => void;

  isOptionSelected: (
    facet?: PresentationFacet | undefined,
    option?: ResponseFacetValue | undefined
  ) => boolean;
  getSelectedOptionsOfFacet: (
    facet: PresentationFacet
  ) => PlpSelectedFacetOption[];
  getUncategorizedSelectedOptions: () => PlpSelectedFacetOption[];
}

const defaultValue =
  {} as unknown as ProductListController_ForFiltering_ConsumerInterface;

const ProductListController_ForFiltering_Context =
  React.createContext(defaultValue);

export const ProductListController_ForFiltering_Provider: React.FunctionComponent =
  (props) => {
    const { selectedOptions, facetDisplayNames } = useProductListState();
    const { setFacetDisplayNames, setSelectedOptions } =
      useProductListState_asController();

    const { presentationFacets } = useProductListPresentationModelFacets();

    const findSelectedOption = (
      facet: PresentationFacet,
      option: ResponseFacetValue
    ) => {
      const thisFacetName = facet.key;
      if (!thisFacetName) {
        return undefined;
      }

      const preexistingSelectedOption = selectedOptions.find(
        ({ facetName, id }) => facetName === thisFacetName && id === option.id
      );

      return preexistingSelectedOption;
    };

    const onNewlySelectedOption = (selectedOption: PlpSelectedFacetOption) => {
      const correspondingFacet = presentationFacets.find(
        (presentationFacet) =>
          presentationFacet.key === selectedOption.facetName
      );

      let newFacetDisplayNames = facetDisplayNames;
      if (correspondingFacet) {
        newFacetDisplayNames = {
          ...newFacetDisplayNames,
          ...{
            [selectedOption.facetName]: [
              correspondingFacet.title,
              correspondingFacet.id,
              correspondingFacet.fredhopperResponseId,
            ],
          },
        };
      }

      setFacetDisplayNames(newFacetDisplayNames);
    };

    const handleActionFilterOption = React.useCallback(
      (facet: PresentationFacet, option: ResponseFacetValue) => {
        let newSelectedOptions = selectedOptions;

        const transformOptionToSelectedOption = ():
          | PlpSelectedFacetOption
          | undefined => {
          let selectedOption: PlpSelectedFacetOption | undefined;
          selectedOption = {
            ...option,
            facetName: facet.key,
            facetIsMultiSelect: facet.hasMultiSelection,
          };

          return selectedOption;
        };

        if (facet.hasMultiSelection) {
          // multi select option / checkbox
          const preexistingSelectedOption = findSelectedOption(facet, option);

          if (preexistingSelectedOption) {
            const thisFacetName = preexistingSelectedOption.facetName;
            const thisID = preexistingSelectedOption.id;

            newSelectedOptions = newSelectedOptions.filter(
              ({ facetName, id }) => {
                if (thisID !== id) {
                  return true;
                }
                if (thisFacetName !== facetName) {
                  return true;
                }
                return false;
              }
            );
          } else {
            const newlySelectedOption = transformOptionToSelectedOption();
            if (newlySelectedOption) {
              newSelectedOptions = [
                ...newSelectedOptions,
                ...[newlySelectedOption],
              ];
              onNewlySelectedOption(newlySelectedOption);
            }
          }
        } else {
          /**
           * Exclusive option selection / radio
           *
           * Basically if the option is already selected in a non multi select facet,
           * do nothing,
           * otherwise, remove the other selected option from this facet and add the new one.
           */
          const preexistingSelectedOption = findSelectedOption(facet, option);
          if (!preexistingSelectedOption) {
            // remove all other options which are potential options of this facet from the currently selected options
            const thisFacetName = facet.key;
            newSelectedOptions = newSelectedOptions.filter(
              ({
                facetName: anySelectedOption_facetName,
                id: anySelectedOption_id,
              }) =>
                !(
                  anySelectedOption_facetName === thisFacetName &&
                  facet.potentialOptions.some(
                    ({ id: optionInThisFacetId }) =>
                      anySelectedOption_id === optionInThisFacetId
                  )
                )
            );

            // add this option into selected options
            const newlySelectedOption = transformOptionToSelectedOption();
            if (newlySelectedOption) {
              newSelectedOptions = [
                ...newSelectedOptions,
                ...[newlySelectedOption],
              ];
              onNewlySelectedOption(newlySelectedOption);
            }
          }
        }

        setSelectedOptions(newSelectedOptions);
      },
      [
        selectedOptions,
        onNewlySelectedOption,
        presentationFacets,
        facetDisplayNames,
        setFacetDisplayNames,
        setSelectedOptions,
      ]
    );

    const handleRemoveSelectedFilterOption = React.useCallback(
      (selectedOption: PlpSelectedFacetOption) => {
        let newSelectedOptions = selectedOptions.filter(
          ({ facetName, id }) =>
            !(
              facetName === selectedOption.facetName && id === selectedOption.id
            )
        );
        setSelectedOptions(newSelectedOptions);
      },
      [selectedOptions, setSelectedOptions]
    );

    const handleRemoveAllSelectedOptions = React.useCallback(() => {
      setSelectedOptions([]);
      setFacetDisplayNames({});
    }, [selectedOptions, setFacetDisplayNames]);

    const handleRemoveSelectedOptionsInBulk = React.useCallback(
      (selectedOptionsToRemove: PlpSelectedFacetOption[]) => {
        const newSelectedOptions = selectedOptions.filter(
          ({ facetName, id }) =>
            !selectedOptionsToRemove.some(
              ({ facetName: facetNameRhs, id: idRhs }) =>
                facetName === facetNameRhs && id === idRhs
            )
        );
        setSelectedOptions(newSelectedOptions);
      },
      [selectedOptions, setSelectedOptions]
    );

    const isOptionSelected = React.useCallback(
      (facet?: PresentationFacet, option?: ResponseFacetValue) => {
        if (!facet || !option) {
          return false;
        }
        const thisFacetName = facet.key;
        if (!thisFacetName) {
          return false;
        }
        const isSelected = selectedOptions.some(
          ({ facetName, id }) => facetName === thisFacetName && id === option.id
        );
        return isSelected;
      },
      [selectedOptions]
    );

    const getSelectedOptionsOfFacet = React.useCallback(
      (facet: PresentationFacet) => {
        let selectedOptionsOfFacet = selectedOptions.filter(
          ({ facetName }) => facetName === facet.key
        );
        return selectedOptionsOfFacet;
      },
      [selectedOptions]
    );

    const getUncategorizedSelectedOptions =
      React.useCallback((): PlpSelectedFacetOption[] => {
        const uncategorizedSelectedOptions = selectedOptions.filter(
          ({ facetName }) =>
            !presentationFacets.some(({ key }) => key === facetName)
        );
        return uncategorizedSelectedOptions;
      }, [selectedOptions, presentationFacets]);

    const value: ProductListController_ForFiltering_ConsumerInterface =
      React.useMemo(
        () => ({
          handleActionFilterOption,
          handleRemoveSelectedFilterOption,
          handleRemoveAllSelectedOptions,
          handleRemoveSelectedOptionsInBulk,

          isOptionSelected,
          getSelectedOptionsOfFacet,
          getUncategorizedSelectedOptions,
        }),
        [
          handleActionFilterOption,
          handleRemoveSelectedFilterOption,
          handleRemoveAllSelectedOptions,
          handleRemoveSelectedOptionsInBulk,

          isOptionSelected,
          getSelectedOptionsOfFacet,
          getUncategorizedSelectedOptions,
        ]
      );
    return (
      <ProductListController_ForFiltering_Context.Provider value={value}>
        {props.children || []}
      </ProductListController_ForFiltering_Context.Provider>
    );
  };

export const useProductListController_forFiltering = () => {
  const context = React.useContext(ProductListController_ForFiltering_Context);
  if (context === defaultValue) {
    throw new Error(
      "useProductListController_forFiltering must be used within a ProductListController_ForFiltering_Provider"
    );
  }
  return context;
};
